import DataStructure, { sanitize } from "./DataStructure";
import { LABOR_EXPENSE_COST_CATEGORIES } from "./QuoteLaborItem";
import {
  deepFreeze,
  formatCurrency,
  formatPercentage,
  roundPrice,
} from "../util/misc.js";
import { cloneDeep, isArray, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const QUOTE_TOTAL_FIELDS = {
  categories: {},
  vendors: {},
  // Expected cost by expense category for costs quoted on labor lines but paid
  // as additional expenses, e.g. `{ Materials, "Equipment Rental" }`. Keys match
  // DEFAULT_EXPENSE_CATEGORIES so the Sale Accounting tab can show expected vs
  // actual per category. Only present when `includeCost`.
  expenseCosts: {},
  items: 0,
  screens: 0,
  hardware: 0,
  // Child-accessory units (sale items carrying a `parentID`). Like `screens`
  // and `hardware`, this is never derived from quote lines — child accessories
  // are only ever created against an existing sale (SaleItemEdit / change
  // order) — so it is maintained incrementally by `bucketContributions`
  // (`src/store/main/actions/sales.js`) and recomputed by `repairSale`.
  accessories: 0,
  cost: 0,
  additionalCost: 0,
  margin: 0,
  subtotal: 0,
  // Sum of labor line prices. Tracked separately so the material-only balance
  // (balance − labor) can be derived without loading the labor subcollection.
  labor: 0,
  tax: 0,
  taxPercent: 0,
  taxCounty: null,
  discount: 0,
  // Sum of the Shipping/Delivery Charge line(s) we bill the customer.
  // Included in `subtotal` and `price` — the customer pays it and it is
  // taxable — but tracked separately so the COMMISSIONABLE total can
  // subtract it. Shipping is pass-through money for the company to pay the
  // carrier with; it must not inflate a salesperson's margin. Note this is
  // the shipping we CHARGE: inbound manufacturer freight is cost of goods
  // and correctly stays in `cost`.
  shipping: 0,
  // Vendor cost carried by the shipping line, if any. Subtracted from the
  // commissionable cost basis alongside the revenue exclusion above, so the
  // shipping line does not participate in the commission calculation at ALL.
  // Zero for a charge entered through the app (QuoteMiscItem.isValid forces
  // cost 0 on a vendor-less line), but a freight line back-filled from
  // history can carry a real vendor cost — and removing its revenue while
  // keeping its cost would UNDERPAY commission by the full cost.
  shippingCost: 0,
  addDiscount: 0,
  fee: 0,
  // Sum of late-payment finance charges accrued across this sale's invoices
  // (see `InvoiceTotal.financeCharge`). Sale-only — always 0 on a quote.
  // Maintained incrementally by the finance-charge assess/remove callables and
  // the daily accrual job in `functions/invoices.js`, and added into
  // `balance`, mirroring how `fee` is handled. Kept out of the commissionable
  // `actualPreTax` so it never affects a salesperson's margin.
  financeCharge: 0,
  credit: 0,
  refunds: 0,
  payments: 0,
  price: 0,
  balance: 0,
  actualPreTax: null,
  actualCost: null,
};
deepFreeze(QUOTE_TOTAL_FIELDS);

export default class QuoteTotal extends DataStructure {
  constructor(data = {}) {
    if (data) {
      data = cloneDeep(data);
      if (isArray(data.items)) {
        data = computeTotal(data);
      }
      if (!("balance" in data)) {
        data.balance = data.price;
      }
    }

    let includeCost = data && "cost" in data;
    data = sanitize(data, QUOTE_TOTAL_FIELDS);
    if (!includeCost) {
      delete data.cost;
      delete data.margin;
      delete data.vendors;
      delete data.expenseCosts;
    }

    super(data);
  }

  // Editing

  compute(data) {
    this.data = computeTotal(data);
  }

  get isEmpty() {
    return isEmpty(diff(this.data, new QuoteTotal().data));
  }

  isEqual(total) {
    return isEmpty(diff(this.data, total.data));
  }

  // Formatting

  get tax() {
    return formatCurrency(this.data.tax);
  }

  get taxPercent() {
    return formatPercentage(100 * this.data.taxPercent);
  }

  get cost() {
    return formatCurrency(this.data.cost);
  }

  get totalCost() {
    return formatCurrency(this.data.cost || 0);
  }

  get price() {
    return formatCurrency(this.data.price);
  }

  get labor() {
    return formatCurrency(this.data.labor);
  }

  get balance() {
    return formatCurrency(this.data.balance);
  }

  get actualPreTax() {
    return this.data.actualPreTax !== null
      ? formatCurrency(this.data.actualPreTax)
      : null;
  }

  get actualCost() {
    return this.data.actualCost !== null
      ? formatCurrency(this.data.actualCost)
      : null;
  }

  get margin() {
    return formatCurrency(this.data.margin || 0);
  }

  get marginPercent() {
    const margin = this.data.margin || 0;
    // Guard the denominator (subtotal − discount) against 0 → avoids Infinity%.
    const base = this.data.subtotal - this.data.discount;
    return this.data.subtotal && margin && base
      ? formatPercentage(100 * (margin / base))
      : null;
  }

  get subtotal() {
    return formatCurrency(this.data.subtotal);
  }

  get discount() {
    return formatCurrency(this.data.discount);
  }

  get addDiscount() {
    return formatCurrency(this.data.addDiscount);
  }

  get fee() {
    return formatCurrency(this.data.fee);
  }

  get financeCharge() {
    return formatCurrency(this.data.financeCharge);
  }

  get credit() {
    return formatCurrency(this.data.credit);
  }

  get refunds() {
    return formatCurrency(this.data.refunds);
  }

  get payments() {
    return formatCurrency(this.data.payments);
  }

  get discountPercent() {
    return formatPercentage(
      this.data.discount && this.data.subtotal
        ? 100 * (this.data.discount / this.data.subtotal)
        : 0
    );
  }

  get addDiscountPercent() {
    const base = this.data.subtotal + this.data.tax - this.data.discount;
    return formatPercentage(
      this.data.addDiscount && base ? 100 * (this.data.addDiscount / base) : 0
    );
  }

  get categories() {
    const categories = {};
    Object.keys(this.data.categories).forEach((category) => {
      categories[category] = formatCurrency(this.data.categories[category]);
    });
    return isEmpty(categories) ? null : categories;
  }

  get vendors() {
    if (!this.data.vendors) {
      return undefined;
    }
    const vendors = {};
    Object.keys(this.data.vendors).forEach((vendor) => {
      vendors[vendor] = formatCurrency(this.data.vendors[vendor]);
    });
    return vendors;
  }

  get expenseCosts() {
    if (!this.data.expenseCosts) {
      return undefined;
    }
    const expenseCosts = {};
    Object.keys(this.data.expenseCosts).forEach((category) => {
      expenseCosts[category] = formatCurrency(this.data.expenseCosts[category]);
    });
    return expenseCosts;
  }

  // Sum of the expected costs quoted on labor lines that get paid as additional
  // expenses (materials, equipment rental).
  get expectedExpenseCost() {
    return roundPrice(
      Object.values(this.data.expenseCosts || {}).reduce(
        (sum, amount) => sum + (amount || 0),
        0
      )
    );
  }
}

// Accumulate an expected cost that will be paid as an expense rather than
// billed by a vendor. Absent/zero amounts are skipped so `expenseCosts` only
// lists categories the quote actually expects to spend in.
function addExpenseCost(total, category, amount, quantity) {
  if (!amount) {
    return;
  }
  if (!(category in total.expenseCosts)) {
    total.expenseCosts[category] = 0;
  }
  total.expenseCosts[category] += amount * quantity;
}

// Compute totals
export function computeTotal({ items, tax, includeCost, payments, original }) {
  const total = cloneDeep(QUOTE_TOTAL_FIELDS);

  if (!includeCost) {
    delete total.cost;
    delete total.margin;
    delete total.vendors;
    delete total.expenseCosts;
  }

  if (payments) {
    total.payments = payments;
  }

  // Transaction-level accumulators are never quote-derived: credit memos,
  // refunds, early-payment discounts and card fees are written incrementally
  // against the SALE (functions/transactions.js::writeTransaction,
  // functions/invoices.js::touchInvoice). A quote-driven recompute has to
  // carry them over from the sale's existing total, exactly as the backend
  // does in `functions/sales-helpers.js::computeQuoteTotals`. Dropping them
  // hid the Refund / Credit Memo / Fees rows from the change-order preview
  // (QuoteTotals.vue gates each row on the value being truthy) and left the
  // previewed balance disagreeing with the balance `_changeSale` saves.
  if (original) {
    total.addDiscount = original.addDiscount || 0;
    total.fee = original.fee || 0;
    total.credit = original.credit || 0;
    total.refunds = original.refunds || 0;
    // Late-payment finance charges accrued on the sale's invoices are written
    // by the finance-charge callables / daily accrual (functions/invoices.js),
    // never quote-derived — carry them like `fee`, and add them back into
    // `balance` below.
    total.financeCharge = original.financeCharge || 0;
  }

  const discount = {
    percentage: 0,
    fixed: 0,
  };

  if (tax === undefined) {
    tax = total.taxPercent || 0;
  }
  if (tax && tax > 1) {
    tax /= 100;
  } else {
    tax = 0;
  }

  // Iterate through items
  let quantity, itemPrice, itemCost, vendorCost, category, vendor;
  let taxableSubtotal = 0;
  items.forEach((item) => {
    if (item.itemType === "discount") {
      // Discount
      if (item.type === "percentage") {
        discount.percentage += item.value;
      } else {
        discount.fixed += item.value;
      }
    } else {
      // Material or Labor
      quantity =
        !item.itemType || item.itemType === "material" ? item.quantity : 1;
      itemPrice = (item.price || 0) * quantity;
      itemCost = (item.cost || 0) * quantity;
      // The vendor bucket tracks only what that vendor is paid. For labor that
      // is the labor cost alone -- the quoted material and equipment-rental
      // portions are reimbursed as expenses, so they go to `expenseCosts` and
      // must stay out of `vendors.Labor` or SaleLaborCosts would compare the
      // labor expenses against an inflated expectation.
      vendorCost = itemCost;
      taxableSubtotal +=
        tax && !(item.notax ?? item.itemType === "labor") ? itemPrice : 0;
      category = item.category || "Other";
      if (item.itemType === "labor") {
        vendor = "Labor";
        total.labor += itemPrice;
        if (includeCost) {
          Object.entries(LABOR_EXPENSE_COST_CATEGORIES).forEach(
            ([field, category]) =>
              addExpenseCost(total, category, item[field], quantity)
          );
        }
        // Total cost of a labor line is labor + materials + equipment rental,
        // matching `laborTotalCost` (src/classes/QuoteLaborItem.js) -- the same
        // basis the sales price is derived from.
        itemCost +=
          Object.keys(LABOR_EXPENSE_COST_CATEGORIES).reduce(
            (sum, field) => sum + (item[field] || 0),
            0
          ) * quantity;
      } else if (item.brand) {
        vendor = item.brand;
      } else {
        vendor = "";
      }
      if (!(category in total.categories)) {
        total.categories[category] = 0;
      }
      if (includeCost && vendor && !(vendor in total.vendors)) {
        total.vendors[vendor] = 0;
      }
      total.categories[category] += itemPrice;
      if (item.itemType === "material") {
        total.items += quantity;
      }
      total.subtotal += itemPrice;
      total.price += itemPrice;
      if (item.isShipping) {
        total.shipping += itemPrice;
      }
      if (includeCost) {
        if (vendor) {
          total.vendors[vendor] += vendorCost;
        }
        total.cost += itemCost;
        if (item.isShipping) {
          total.shippingCost += itemCost;
        }
      }
    }
  });

  // Clean up
  if (
    "Other" in total.categories &&
    Object.keys(total.categories).length === 1
  ) {
    total.categories = {};
  } else {
    Object.keys(total.categories).forEach((category) => {
      total.categories[category] = roundPrice(total.categories[category]);
    });
  }
  if (includeCost) {
    Object.keys(total.vendors).forEach(
      (vendor) => (total.vendors[vendor] = roundPrice(total.vendors[vendor]))
    );
    Object.keys(total.expenseCosts).forEach(
      (category) =>
        (total.expenseCosts[category] = roundPrice(
          total.expenseCosts[category]
        ))
    );
  }
  total.discount = roundPrice(
    discount.fixed + (total.subtotal * discount.percentage) / 100
  );

  total.tax = roundPrice(tax * Math.max(0, taxableSubtotal - total.discount));
  total.price = roundPrice(total.price - total.discount + total.tax);

  if (includeCost) {
    total.cost = roundPrice(total.cost);
    total.margin = roundPrice(total.subtotal - total.discount - total.cost);
  }
  total.taxPercent = tax;
  total.subtotal = roundPrice(total.subtotal);
  total.labor = roundPrice(total.labor);
  total.balance = roundPrice(
    total.price -
      (total.credit || 0) -
      total.payments +
      (total.refunds || 0) -
      total.addDiscount +
      total.fee +
      (total.financeCharge || 0)
  );
  return total;
}
